import type { Polygon } from "geojson";
import * as turf from "@turf/turf";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { AoiMapRender } from "../map/AoiMapRender";
import type { NotificacionPiezas } from "../map/AoiDrawToolbar";
import type { OperacionVerticeAoi } from "../map/AoiSnapEditEngine";
import {
  fx_depurarPoligonos,
  fx_mensajeDepuracion,
  fx_nombreDePieza,
  PoligonoConNombre,
} from "../map/depurarPoligonos";
import { fx_piezasDesdeLotes } from "../map/reconciliarLotes";
import { grpcFincaLoteDatabase } from "../../../../../modulos/terceros/database/GrpcFincaLoteDatabase";
import {
  RegistroFincaBorrador,
  RegistroFincaBorradorLote,
} from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";
import { registroFincaTempGrpcService } from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import {
  parseWizardError,
  parseWizardErrorMessage,
} from "../../../../../modulos/terceros/wizard/response/WizardValidationErrorResponse";
import { _miles } from "../../../../../utils/numberUtils";
import {
  BaseRegistroFincaPasoRender,
  RegistroFincaFooterOptions,
} from "./BaseRegistroFincaPasoRender";
import template from "./registroFincaPaso3Template.html";

/** Margen de tolerancia sobre el máximo de hectáreas del plan — pedido
 *  explícito: superar el máximo ya pinta el badge rojo y avisa por cuánto,
 *  pero "Continuar" recién bloquea si el exceso supera este % del máximo
 *  (no bloquea con cualquier exceso mínimo, ej. ruido de precisión al
 *  dibujar el borde). */
const HECTAREAS_MARGEN = 0.05;

/** Nombre del paso, que el mapa pinta como primer card del aviso, al lado del
 *  de la herramienta activa (ver `AoiMapRender`, constante `AVISOS`). Vive acá
 *  y no en el mapa: es el enunciado de ESTE paso del wizard, no algo que el
 *  componente de mapa pueda saber. */
const AVISO_PASO = "1) Dibuje el área global del predio.";

/** Paso 3 del wizard de registro: dibujar (o corregir) el área de la finca
 *  sobre el mapa.
 *
 *  **Todo sale y entra de la base.** Al montar se lee el registro temporal
 *  (`leerAsync`): el área guardada y si ya hay lotes. Nada se arrastra en
 *  memoria desde otro paso, salvo los polígonos que el paso 2 acaba de
 *  importar de un archivo (con sus nombres), que todavía no son lotes.
 *
 *  Qué se muestra, en este orden: los polígonos recién importados del paso
 *  2; si no hay, los **polígonos padre guardados** (`poligonosPadre`), que son
 *  las piezas tal cual las dibujó o importó el usuario; si el registro no los
 *  tiene —es anterior a esa columna—, las piezas que salen de los lotes
 *  guardados (`fx_piezasDesdeLotes`: cada grupo de lotes que se tocan fundido
 *  en UNA pieza —el polígono que era antes de subdividirlo— más lo que el área
 *  tenga fuera de los lotes); sin lotes, el área guardada como una pieza; si
 *  tampoco hay área, el mapa en blanco.
 *
 *  **Nunca se restaura desde `aoi_satelite` habiendo polígonos padre**: el AOI
 *  es la UNIÓN de las piezas, y unir es irreversible — restaurarlo devolvía un
 *  contorno fundido donde el usuario había dibujado polígonos separados, que
 *  ya no podía editar ni borrar por separado. `fx_piezasDesdeLotes` era una
 *  reconstrucción aproximada de eso mismo; sigue como respaldo para los
 *  registros que empezaron antes de que las piezas se guardaran.
 *
 *  **El perímetro se guarda al terminar cada polígono, no al pulsar
 *  "Continuar".** Cada vez que se cierra, se edita o se borra un polígono
 *  (`_onPiezasChange`, después del montaje) se recalcula el perímetro global
 *  y se guarda (`GuardarAoi`, mismas reglas que la validación de geometría
 *  de siempre). Los guardados van en cola y se colapsan. Guardar el área NO
 *  borra los lotes: eso se decide al confirmar.
 *
 *  **"Continuar" manda las piezas al servidor** (`ConfirmarPiezas`),
 *  depuradas por `fx_depurarPoligonos` (sin inválidas, repetidas ni
 *  solapadas) y cada una con su marca de modificada (`getPiezas`: nueva o
 *  editada con Editar). El servidor decide contra la base:
 *  - pieza SIN modificar cuyos lotes la cubren en más del 99%: ya está
 *    subdividida, se CONSERVAN sus lotes y la pieza no se agrega;
 *  - pieza modificada (más grande o más chica): pierde sus lotes y queda
 *    como un lote;
 *  - pieza sin lotes: se agrega como lote.
 *  Así, agregar polígonos al área no deshace la subdivisión de los que ya
 *  estaban. Cada lote nuevo lleva el nombre del polígono importado que le
 *  corresponde (`fx_nombreDePieza`) o "Lote N". Recién después confirma el
 *  paso (`AvanzarPaso(3)`).
 *
 *  También muestra (si el plan no es demo), al lado del avance de pasos en
 *  la barra del modal, un badge con las hectáreas que **quedan disponibles**
 *  del plan — máximo menos (las fincas YA EXISTENTES del cliente + el área
 *  que se está dibujando en vivo), recalculado en cada cambio. El área en
 *  vivo sale de SUMAR las piezas, no de unirlas. En plan demo
 *  (`_maximoHectareas === null`) el badge nunca se muestra. Si el exceso
 *  supera `HECTAREAS_MARGEN` del máximo, "Continuar" se deshabilita (rojo =
 *  alerta temprana, bloqueo = límite duro con margen). */

/** El rectángulo que contiene a todas las piezas, como polígono.
 *
 *  Es el área a refrescar en la capa WMS después de guardar. Antes se usaba el
 *  perímetro unido, que ya no se calcula en el cliente; el sobre cubre lo
 *  mismo, cuesta O(vértices) y evita refrescar la capa entera (que se ve como
 *  una recarga del mapa). `null` sin piezas: ahí no hay nada que refrescar
 *  acotado y la capa se recarga completa, que es lo correcto al quedarse sin
 *  área. */
function fx_sobreDeLasPiezas(piezas: readonly Polygon[]): Polygon | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const pieza of piezas) {
    for (const anillo of pieza.coordinates) {
      for (const [x, y] of anillo) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!Number.isFinite(minX)) return null;
  return {
    type: "Polygon",
    coordinates: [
      [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
        [minX, minY],
      ],
    ],
  };
}

export class RegistroFincaPaso3Render extends BaseRegistroFincaPasoRender {
  private _aoiMapRender: AoiMapRender | null = null;
  private _disposed = false;
  private _fincasExistentesHectareas = 0;
  private _hasShapes = false;
  /** Se pasó del máximo del plan (dispara el badge rojo) — independiente de
   *  si eso alcanza a bloquear "Continuar" (`_bloqueaContinuar`, con margen). */
  private _hectareasExcedidas = false;
  /** Exceso más allá del margen de tolerancia — esto sí bloquea "Continuar". */
  private _bloqueaContinuar = false;
  /** Recién después del montaje los cambios de piezas son del usuario: el
   *  montaje notifica las piezas restauradas, y eso no es algo que guardar. */
  private _montado = false;
  /** Cambios del dibujo (se incrementa en cada uno) y último que se guardó
   *  bien. Distintos = hay algo sin guardar. */
  private _version = 0;
  private _versionGuardada = 0;
  /** Cola de guardados del perímetro. */
  private _guardado: Promise<void> = Promise.resolve();
  /** Hay un cambio del DIBUJO sin guardar (un polígono terminado, editado o
   *  borrado con la herramienta). Es lo que decide si, al guardar, hay que
   *  volver a pedirle los tiles a GeoServer: esos cambios todavía no están en
   *  la base y la capa no los muestra.
   *
   *  Un vértice movido NO lo levanta: ese ya se guardó por su cuenta
   *  (`EditarVerticePoligonoPadre`) y su repintado ya se pidió ahí. Lo que
   *  queda pendiente de él es sólo el perímetro, que el servidor recalcula y
   *  que **nunca vuelve al cliente** — refrescar la capa por eso es el salto
   *  que se veía al pulsar "Continuar". */
  private _cambiosDeDibujoSinGuardar = false;
  /** Estado del registro en la base: si tiene área y sus lotes. */
  private _hayAreaGuardada = false;
  private _lotes: RegistroFincaBorradorLote[] = [];

  /** @param _importados Polígonos recién importados de un archivo en el paso
   *   2, depurados y con sus nombres. Vacío si no se importó nada ahora
   *   (dibujo a mano, o se volvió a este paso desde el 4). */
  constructor(
    private readonly _borrador: RegistroFincaBorrador,
    private readonly _importados: PoligonoConNombre[],
    private readonly _maximoHectareas: number | null,
    private readonly _onHectareasBadgeChange: (
      text: string | null,
      isError?: boolean,
    ) => void,
    private readonly _onVolver: () => void,
    private readonly _onGeometriaGuardada: (borrador: RegistroFincaBorrador) => void,
  ) {
    super(_borrador.clienteId);
  }

  renderBody(): string {
    return template;
  }

  connectBody(): void {
    const mapMount = document.getElementById("registroFincaPaso3MapMount");
    if (!mapMount) return;

    // Se tapa el paso DESDE YA, antes de la lectura: el paso 3 se monta con el
    // mapa todavía vacío y lo que viene después —leer el registro, montar el
    // mapa, el ticket, los tiles— tarda. Viniendo de importar un archivo esta
    // capa es la continuación de la de "Importando archivo" del paso 2, que se
    // apaga sola al cambiar de paso; sin esto quedaba un hueco en el que el
    // usuario veía el mapa vacío y los polígonos aparecían encima.
    //
    // La apaga `_destaparCuandoElMapaMuestreLoGuardado`.
    this._taparMapa(mapMount);

    Promise.all([
      registroFincaTempGrpcService.leerAsync(this._borrador).catch((error) => {
        serviceProvider
          .getErrorService()
          .addError("Error al leer el registro de la finca", parseWizardErrorMessage(error));
        return null;
      }),
      grpcFincaLoteDatabase.getFincasAsync(),
    ]).then(([leido, fincas]) => {
      if (this._disposed) return;

      this._hayAreaGuardada = !!leido?.aoi;
      this._lotes = leido?.lotes ?? [];
      if (leido) {
        this._borrador.ultimoPaso = leido.borrador.ultimoPaso;
        this._borrador.tieneArea = leido.borrador.tieneArea;
        this._borrador.aoiOrigen = leido.borrador.aoiOrigen;
      }

      this._fincasExistentesHectareas = fincas.reduce(
        (total, f) => total + f.hectareasFinca,
        0,
      );

      const piezasIniciales: Polygon[] =
        this._importados.length > 0
          ? this._importados.map((p) => p.polygon)
          : (leido?.poligonosPadre.length ?? 0) > 0
            ? leido!.poligonosPadre
            : fx_piezasDesdeLotes(this._lotes, leido?.aoi ?? null);

      // De las tres fuentes de arriba, SÓLO `poligonosPadre` está en la base, y
      // por lo tanto sólo ésa la pinta la capa WMS. Las otras dos se arman en el
      // cliente —un KML recién importado en el paso 2, o las piezas
      // reconstruidas de los lotes— y no existen en `poligonos_padre` hasta el
      // primer guardado: ésas las tiene que pintar Leaflet mientras tanto, o el
      // mapa se vería vacío.
      //
      // Hasta acá no importaba porque Leaflet pintaba las tres, encima del WMS
      // que ya pintaba la primera. Ahora que las piezas guardadas las renderiza
      // sólo el WMS, hay que distinguirlas.
      const piezasEnLaBase =
        this._importados.length === 0 &&
        (leido?.poligonosPadre.length ?? 0) > 0;

      this._aoiMapRender = new AoiMapRender(
        piezasIniciales,
        this._borrador.aoiOrigen !== "kml",
        AVISO_PASO,
        // Habilita la capa WMS de polígonos padre: la Api filtra por este id.
        this._borrador.registroId ?? null,
        piezasEnLaBase,
        // Los ids de esas piezas, para poder pedirle al servidor los vértices
        // de la que el usuario seleccione en modo Editar. Sólo valen si las
        // piezas son las de la base: un KML o una reconstrucción de los lotes
        // no tiene filas en `tbl_temp_poligonos_padre` todavía.
        piezasEnLaBase ? (leido?.poligonosPadreIds ?? []) : [],
      );
      // Cablear ANTES de connect(): el primer disparo (arranca en `false`/
      // sin piezas) pasa dentro de connect() →
      // AoiDrawToolbar.init() → _refreshAvailability()/_notifyPiezasChange().
      this._aoiMapRender.setOnDrawAvailabilityChange(
        this._onDrawAvailabilityChange,
      );
      this._aoiMapRender.setOnPiezasChange(this._onPiezasChange);
      this._aoiMapRender.setOnEditarVertice(this._onEditarVertice);
      // `innerHTML` se lleva por delante la capa que tapa el mapa, así que se
      // vuelve a poner enseguida — y encima del mapa, que es donde tiene que
      // estar.
      mapMount.innerHTML = this._aoiMapRender.render();
      this._aoiMapRender.connect();
      this._taparMapa(mapMount);
      // Sólo hay que esperar al WMS cuando hay piezas GUARDADAS, que son las
      // únicas que pinta él. Por el camino "Dibujar" no hay nada que mostrar,
      // y unas piezas que todavía no están en la base ya las pintó Leaflet
      // dentro de `connect()`: en los dos casos el mapa ya está completo y
      // seguir tapándolo sería hacer esperar de más.
      if (piezasEnLaBase && piezasIniciales.length > 0) {
        this._destaparCuandoElMapaMuestreLoGuardado();
      } else {
        document.getElementById(RegistroFincaPaso3Render.CARGANDO_ID)?.remove();
      }
      this._montado = true;
    }).catch(() => {
      // Si la preparación del paso falla, el mapa no llega a montarse y nadie
      // va a avisar que pintó: hay que destapar igual, o la pantalla queda
      // tapada para siempre. El error en sí ya lo informó quien correspondía.
      document.getElementById(RegistroFincaPaso3Render.CARGANDO_ID)?.remove();
    });
  }

  /** Pone (o repone) la capa que tapa el mapa mientras no muestre lo guardado.
   *  Es la misma capa de carga del paso 2, con su spinner, para que el cambio
   *  de paso no se note como un parpadeo. */
  private _taparMapa(mapMount: HTMLElement): void {
    if (this._disposed) return;
    if (document.getElementById(RegistroFincaPaso3Render.CARGANDO_ID)) return;
    mapMount.insertAdjacentHTML(
      "beforeend",
      `<div class="crear-finca-cargando crear-finca-cargando--mapa" id="${RegistroFincaPaso3Render.CARGANDO_ID}">
         <div class="crear-finca-cargando-card">
           <span class="crear-finca-spinner" aria-hidden="true"></span>
           <span>Cargando el área</span>
         </div>
       </div>`,
    );
  }

  /** Destapa el mapa cuando el WMS ya pintó las piezas guardadas.
   *
   *  **Con tope de tiempo.** Lo que se espera es una imagen de red; si no
   *  llegara —ticket rechazado, GeoServer caído— dejar la pantalla tapada
   *  sería peor que mostrarla vacía, porque el paso sigue siendo usable: se
   *  puede dibujar igual. */
  private _destaparCuandoElMapaMuestreLoGuardado(): void {
    const destapar = (): void => {
      if (this._destapeTimer !== null) {
        clearTimeout(this._destapeTimer);
        this._destapeTimer = null;
      }
      document.getElementById(RegistroFincaPaso3Render.CARGANDO_ID)?.remove();
    };

    this._destapeTimer = window.setTimeout(
      destapar,
      RegistroFincaPaso3Render.ESPERA_MAXIMA_MAPA_MS,
    );
    this._aoiMapRender?.alPintarLoGuardado(destapar);
  }

  /** Id de la capa de carga del mapa. */
  private static readonly CARGANDO_ID = "registroFincaPaso3Cargando";

  /** Cuánto se tapa el mapa como máximo (ver
   *  `_destaparCuandoElMapaMuestreLoGuardado`). */
  private static readonly ESPERA_MAXIMA_MAPA_MS = 10000;

  private _destapeTimer: number | null = null;

  disposeBody(): void {
    this._disposed = true;
    if (this._destapeTimer !== null) {
      clearTimeout(this._destapeTimer);
      this._destapeTimer = null;
    }
    this._aoiMapRender?.dispose();
  }

  getFooterOptions(): RegistroFincaFooterOptions {
    return {
      volver: { onClick: this._onVolverClick },
      submit: {
        label: "Continuar →",
        // Apagado hasta que el mapa se monte con algo dibujado: de ahí en más
        // lo maneja `_refreshSubmitDisabled` con `_hasShapes`.
        disabled: true,
        onClick: this._onSubmitClick,
      },
    };
  }

  /** Habilita/deshabilita "Continuar" directo por DOM (los botones del
   *  footer son fijos en el template del modal, no hay un re-render reactivo
   *  de `getFooterOptions()`). */
  private _onDrawAvailabilityChange = (hasShapes: boolean): void => {
    this._hasShapes = hasShapes;
    this._refreshSubmitDisabled();
  };

  /** Cada cambio de piezas: guarda el perímetro (si es del usuario) y
   *  recalcula el badge de hectáreas disponibles. */
  /** El usuario movió, insertó o quitó un vértice de una pieza: viaja la
   *  OPERACIÓN (un punto), no la geometría.
   *
   *  El servidor la aplica sobre la pieza guardada, valida que no quede
   *  cruzada ni pisando a una vecina y devuelve la geometría que quedó en la
   *  base. Un rechazo no cambió nada allá: se avisa y el mapa vuelve solo a la
   *  forma guardada.
   *
   *  **El perímetro no se guarda acá.** La unión con puentes es del cliente y
   *  sale con `GuardarAoi` cuando el usuario termina —"Continuar" y "Volver"
   *  esperan lo pendiente—, no una vez por vértice: mandar el área entera en
   *  cada arrastre es lo que hacía que la edición se sintiera trabada. */
  private _onEditarVertice = async (
    poligonoPadreId: string,
    operacion: OperacionVerticeAoi,
  ): Promise<Polygon | null> => {
    const registroId = this._borrador.registroId;
    if (!registroId) return null;

    try {
      const piezas =
        await registroFincaTempGrpcService.editarVerticePoligonoPadreAsync(
          registroId,
          {
            poligonoPadreId,
            origen: operacion.origen,
            destino: operacion.destino,
            indiceArista: operacion.insercion?.indiceArista,
          },
        );
      if (this._disposed) return null;
      // El área cambió: lo confirmado del paso 3 en adelante ya no vale. El
      // servidor baja `ultimo_paso` por su cuenta; acá se espeja el borrador.
      this._borrador.ultimoPaso = Math.min(this._borrador.ultimoPaso, 2);

      const editada =
        piezas.find((pieza) => pieza.id === poligonoPadreId)?.polygon ?? null;
      // La pieza cambió de forma EN LA BASE, así que hay que volver a pedirle
      // los tiles a GeoServer: es la misma capa que la pinta, y sin esto
      // seguiría mostrando el contorno de antes del vértice. Acotado al área de
      // la pieza, como después de cualquier guardado.
      if (editada) this._aoiMapRender?.refrescarPoligonosPadre(editada);
      return editada;
    } catch (error) {
      if (this._disposed) return null;
      serviceProvider
        .getErrorService()
        .addWarning(
          "No se pudo editar el vértice",
          parseWizardErrorMessage(error),
        );
      return null;
    }
  };

  private _onPiezasChange = (
    piezas: Polygon[],
    opciones?: NotificacionPiezas,
  ): void => {
    // Polígono terminado, editado o borrado por el usuario: se guarda el
    // perímetro. NO se guarda lo que notifica el montaje (piezas restauradas)
    // ni una recarga desde la base: en los dos casos lo que entró al modelo
    // ACABA de salir del servidor, así que mandarlo de vuelta sería pedirle que
    // guarde lo que ya tiene — y después de un rechazo, además, encadenaría otro
    // guardado justo cuando lo que hace falta es parar.
    //
    // `geometriaGuardada` es el caso del vértice editado: la PIEZA ya está en
    // la base (la guardó `EditarVerticePoligonoPadre`), pero el perímetro —que
    // es la unión con puentes, y la arma el cliente— todavía no. Se cuenta como
    // pendiente sin encolar nada: lo manda el "Continuar" (o el "Volver"), una
    // sola vez, en vez de una por vértice.
    if (this._montado && !this._recargando) {
      if (opciones?.geometriaGuardada) {
        this._version++;
      } else {
        this._cambiosDeDibujoSinGuardar = true;
        this._programarGuardado();
      }
    }

    if (this._maximoHectareas === null) return;

    // El área sale de SUMAR las piezas, no de unirlas: unir es caro y el
    // motor ya no permite que se solapen, así que la suma es el área real
    // (la unión solo agrega los puentes, de área despreciable).
    let areaActual = 0;
    for (const pieza of piezas) {
      try {
        areaActual += turf.area(pieza) / 10000;
      } catch {
        // Geometría inválida: no suma.
      }
    }

    const disponibles =
      this._maximoHectareas - this._fincasExistentesHectareas - areaActual;
    this._hectareasExcedidas = disponibles < 0;
    this._bloqueaContinuar =
      this._hectareasExcedidas &&
      Math.abs(disponibles) > this._maximoHectareas * HECTAREAS_MARGEN;
    this._onHectareasBadgeChange(
      this._hectareasExcedidas
        ? `Excede el plan por ${_miles(Math.abs(disponibles))} ha`
        : `${_miles(disponibles)} ha disponibles`,
      this._hectareasExcedidas,
    );
    this._refreshSubmitDisabled();
  };

  private _refreshSubmitDisabled(): void {
    const btnSubmit = document.getElementById(
      "registroFincaSubmit",
    ) as HTMLButtonElement | null;
    if (btnSubmit) {
      btnSubmit.disabled = !this._hasShapes || this._bloqueaContinuar;
    }
  }

  /** Encola un guardado del perímetro. Al ejecutarse, si llegaron cambios
   *  más nuevos se saltea: el guardado de esos va a leer el mapa más actual. */
  private _programarGuardado(): void {
    const version = ++this._version;
    this._guardado = this._guardado.then(async () => {
      if (version === this._version) await this._guardarVersionAsync(version);
    });
  }

  /** Calcula el perímetro con el mapa tal como está AHORA y lo guarda. Un
   *  error se avisa y no corta la cola: el próximo cambio (o "Continuar")
   *  vuelve a intentarlo. Devuelve si quedó guardado. */
  private async _guardarVersionAsync(version: number): Promise<boolean> {
    const registroId = this._borrador.registroId;
    // Con el paso ya desmontado (se cerró el modal con un guardado en cola)
    // el mapa no tiene piezas: guardar ahora mandaría un área VACÍA y borraría
    // el perímetro del servidor.
    if (!registroId || !this._aoiMapRender || this._disposed) return false;

    const piezas = this._aoiMapRender.getIndividualGeometries();
    // Se lee ANTES del guardado: si mientras viaja el usuario dibuja algo, ese
    // cambio levanta la bandera de nuevo y se la lleva el guardado siguiente.
    const veniaDelDibujo = this._cambiosDeDibujoSinGuardar;

    try {
      // **Sólo viajan las piezas.** El perímetro —la unión topológica con
      // puentes— lo calcula el servidor a partir de ellas. Era el trabajo más
      // pesado del paso y corría acá: con un área importada de un KML tardaba
      // segundos con el hilo principal bloqueado, y como el "Continuar" espera
      // el guardado, la pantalla se congelaba en el cambio de paso.
      const { lotesEliminados, areaCambio, poligonosPadreIds } =
        await registroFincaTempGrpcService.guardarAoiAsync(
          registroId,
          piezas,
          false,
          // Los ids que el mapa ya tiene, para que el servidor actualice las
          // MISMAS filas en vez de estrenar una por pieza en cada guardado.
          // Sin esto, mover un vértice le cambiaría el id a la pieza y el modo
          // Editar se quedaría pidiendo los vértices de una fila eliminada.
          this._aoiMapRender.getPiezasIds(),
        );
      // **Los tiles se vuelven a pedir SÓLO si el dibujo cambió.** Un polígono
      // terminado, editado o borrado todavía no está en la capa, así que hay
      // que repintar el área que acaba de entrar (el sobre de las piezas) y
      // adoptar los ids: una pieza recién dibujada estrena el suyo acá, y es
      // lo que la vuelve editable por vértices.
      //
      // Si no cambió el dibujo, este guardado sólo recalculó el PERÍMETRO del
      // lado del servidor —que no vuelve al cliente y que la capa de polígonos
      // padre ni siquiera pinta—, así que refrescar no tiene nada que mostrar:
      // se veía como un salto de la capa al pulsar "Continuar", por un cambio
      // que no existe. Los ids se adoptan igual, sin pedir tiles.
      if (veniaDelDibujo) {
        this._cambiosDeDibujoSinGuardar = false;
        this._aoiMapRender?.refrescarPoligonosPadre(
          fx_sobreDeLasPiezas(piezas),
          poligonosPadreIds,
        );
      } else {
        this._aoiMapRender?.adoptarIdsDePiezas(poligonosPadreIds);
      }
      this._hayAreaGuardada = piezas.length > 0;
      this._borrador.tieneArea = piezas.length > 0;
      // Solo pasa al quitar el área entera: el servidor borró los lotes.
      if (lotesEliminados) this._lotes = [];
      if (areaCambio) {
        this._borrador.ultimoPaso = Math.min(this._borrador.ultimoPaso, 2);
      }
      this._versionGuardada = version;
      return true;
    } catch (error) {
      // El error trae el JSON (`{ success, code, message }`), no texto plano.
      const detalle = parseWizardError(error);

      // `GEOMETRIA_INVALIDA` al editar vértices = el perímetro quedó cruzado
      // consigo mismo (tramos en X). Ese rechazo NO se puede anticipar desde el
      // cliente —no es una relación con otra geometría, así que no hay filtro
      // espacial que lo pregunte— y lo decide el `IsValid` del servidor. Para
      // cuando llega, el vértice ya se movió en el modelo, así que respetar el
      // rechazo es deshacer la operación.
      //
      // Va por el aviso del mapa y no por el servicio de errores: es una regla
      // de la herramienta, del mismo tipo que "no se puede pisar otra pieza", y
      // se lee al lado del mapa donde acaba de pasar. Un toast de error para
      // algo que el usuario puede corregir moviendo el vértice de vuelta es
      // demasiado ruido.
      // El deshacer local alcanza SÓLO si hay una edición de vértices que
      // deshacer. Si no la hay, el modelo quedó diciendo algo que la base
      // rechazó y hay que traerlo de vuelta de la base — ver
      // `_recargarPiezasDeLaBaseAsync`.
      if (
        detalle?.code === "GEOMETRIA_INVALIDA" &&
        this._aoiMapRender?.revertirEdicionRechazada(detalle.message)
      ) {
        return false;
      }

      serviceProvider
        .getErrorService()
        .addError(
          "Error al guardar el área de la finca",
          parseWizardErrorMessage(error),
        );
      // Cualquier rechazo que no se pudo deshacer deja el modelo y la base
      // diciendo cosas distintas. Sin esto, el guardado siguiente volvía a
      // mandar la misma geometría rechazada y volvía a fallar: el error se
      // "heredaba" a cada vértice que el usuario tocara después, y los handles
      // quedaban sobre una forma que el WMS no estaba dibujando.
      await this._recargarPiezasDeLaBaseAsync();
      return false;
    }
  }

  /** Vuelve a leer las piezas del registro y pisa con ellas lo que tenga el
   *  mapa.
   *
   *  **Manda la base, nunca la memoria.** Es la misma regla que el paso 4 aplica
   *  cuando una operación de lotes falla (`LotesTempSync`): seguir trabajando
   *  sobre un modelo que el servidor rechazó sólo empeora las cosas, porque cada
   *  cambio posterior se manda junto con lo que ya estaba mal.
   *
   *  Si la relectura falla también, no se toca nada: es preferible dejar el mapa
   *  como está —el usuario puede volver a intentar— que vaciarlo. */
  private async _recargarPiezasDeLaBaseAsync(): Promise<void> {
    try {
      const leido = await registroFincaTempGrpcService.leerAsync(this._borrador);
      if (this._disposed || !this._aoiMapRender) return;

      this._recargando = true;
      try {
        this._aoiMapRender.reemplazarPiezasDeLaBase(
          leido.poligonosPadre,
          leido.poligonosPadreIds,
        );
      } finally {
        this._recargando = false;
      }
      this._hayAreaGuardada = !!leido.aoi;
      this._borrador.tieneArea = !!leido.aoi;
      // La versión guardada se pone al día con lo que acaba de entrar: si no, el
      // guardado en cola seguiría viendo una versión pendiente y volvería a
      // mandar.
      this._versionGuardada = this._version;
    } catch {
      // No-op: ver el resumen.
    }
  }

  /** Encendido mientras una recarga desde la base pisa las piezas del modelo,
   *  para que ese cambio no dispare un guardado (ver `_onPiezasChange`). */
  private _recargando = false;

  /** "Volver" espera a que se guarde lo último dibujado. */
  private _onVolverClick = (): void => {
    this._guardado.then(() => this._onVolver());
  };

  private _onSubmitClick = (): void => {
    const registroId = this._borrador.registroId;
    const piezas = this._aoiMapRender?.getIndividualGeometries() ?? [];
    if (!registroId || piezas.length === 0) return;

    const btnSubmit = document.getElementById(
      "registroFincaSubmit",
    ) as HTMLButtonElement | null;
    if (btnSubmit) btnSubmit.disabled = true;

    this._guardado
      .then(async () => {
        // El último guardado falló, o el área todavía no está en la base:
        // reintentar antes de seguir.
        const pendiente =
          this._versionGuardada !== this._version || !this._hayAreaGuardada;
        if (pendiente && !(await this._guardarVersionAsync(this._version))) {
          throw null;
        }
        await this._sincronizarLotesAsync(registroId);
        await registroFincaTempGrpcService.avanzarPasoAsync(registroId, 3);
      })
      .then(() => {
        this._borrador.ultimoPaso = Math.max(this._borrador.ultimoPaso, 3);
        this._onGeometriaGuardada(this._borrador);
      })
      .catch((error) => {
        // `null`: el guardado del perímetro ya avisó su propio error.
        if (error !== null) {
          serviceProvider
            .getErrorService()
            .addError(
              "Error al guardar los lotes",
              parseWizardErrorMessage(error),
            );
        }
        if (btnSubmit) btnSubmit.disabled = false;
      });
  };

  /** Deja los lotes de la base alineados con las piezas del mapa: depura
   *  las piezas (sin inválidas, repetidas ni solapadas) y las manda con su
   *  marca de modificada; el servidor decide, contra la base, qué lotes se
   *  conservan, cuáles se borran y cuáles se agregan (`ConfirmarPiezas`). */
  private async _sincronizarLotesAsync(registroId: string): Promise<void> {
    const piezas = this._aoiMapRender?.getPiezas() ?? [];
    const depuracion = fx_depurarPoligonos(
      piezas.map((pieza) => ({
        polygon: pieza.polygon,
        nombre: fx_nombreDePieza(pieza.polygon, this._importados),
        modificada: pieza.modificada,
      })),
    );
    if (depuracion.validos.length === 0) {
      throw new Error("No hay ningún polígono válido para crear los lotes.");
    }

    const aviso = fx_mensajeDepuracion(depuracion);
    if (aviso) {
      serviceProvider
        .getErrorService()
        .addWarning("Polígonos descartados al crear los lotes", aviso);
    }

    await registroFincaTempGrpcService.confirmarPiezasAsync(
      registroId,
      depuracion.validos.map((pieza) => ({
        polygon: pieza.polygon,
        nombre: pieza.nombre,
        // Sin marca = pieza que no vino del mapa: se trata como nueva.
        modificada: pieza.modificada ?? true,
      })),
    );
  }
}
